/**
 * Resource Preloader Utility
 * Preloads API endpoints and resources for better performance
 */

class ResourcePreloader {
  constructor() {
    this.cache = new Map();
    this.priorities = {
      HIGH: 1,
      MEDIUM: 2,
      LOW: 3
    };
    this.preloadQueue = [];
    this.isProcessing = false;
  }

  /**
   * Preload resources with priority
   */
  async preload(urls, priority = this.priorities.MEDIUM) {
    if (!Array.isArray(urls)) {
      urls = [urls];
    }

    // Add to queue with priority
    urls.forEach(url => {
      if (!this.cache.has(url)) {
        this.preloadQueue.push({ url, priority });
      }
    });

    // Sort by priority
    this.preloadQueue.sort((a, b) => a.priority - b.priority);

    // Process queue if not already processing
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Process the preload queue
   */
  async processQueue() {
    this.isProcessing = true;

    while (this.preloadQueue.length > 0) {
      const { url } = this.preloadQueue.shift();
      
      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.cache.set(url, {
            data,
            timestamp: Date.now(),
            ttl: 5 * 60 * 1000 // 5 minutes
          });
          console.log(`📦 Preloaded: ${url}`);
        }
      } catch (error) {
        console.warn(`⚠️ Failed to preload ${url}:`, error.message);
      }

      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.isProcessing = false;
  }

  /**
   * Get cached data if available and not expired
   */
  getCached(url) {
    const cached = this.cache.get(url);
    if (!cached) return null;

    // Check if expired
    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(url);
      return null;
    }

    return cached.data;
  }

  /**
   * Preload resources for specific routes
   */
  preloadForRoute(route) {
    const routeResources = {
      roster: [
        '/api/it-roster',
        '/api/rates',
        '/api/outcomes'
      ],
      planning: [
        '/api/plans',
        '/api/outcomes',
        '/api/applications'
      ],
      forecast: [
        '/api/outcomes',
        '/api/applications'
      ],
      admin: [
        '/api/applications',
        '/api/owners',
        '/api/tags',
        '/api/rates'
      ]
    };

    const resources = routeResources[route];
    if (resources) {
      this.preload(resources, this.priorities.HIGH);
      console.log(`🚀 Preloading resources for ${route} route`);
    }
  }

  /**
   * Clear expired cache entries
   */
  clearExpired() {
    const now = Date.now();
    for (const [url, cached] of this.cache.entries()) {
      if (now - cached.timestamp > cached.ttl) {
        this.cache.delete(url);
      }
    }
  }

  /**
   * Clear all cache
   */
  clearAll() {
    this.cache.clear();
    this.preloadQueue = [];
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      queueSize: this.preloadQueue.length,
      isProcessing: this.isProcessing
    };
  }
}

// Create singleton instance
const resourcePreloader = new ResourcePreloader();

// Clean up expired cache every 5 minutes
setInterval(() => {
  resourcePreloader.clearExpired();
}, 5 * 60 * 1000);

export default resourcePreloader;